import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { ErrandServiceType, useCreateErrand } from '@/api/hooks/useCreateErrand'
import { useServiceGroups } from '@/api/hooks/useServiceGroups'
import { SubscribeDialog } from '@/components/organisms/subscribe-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollable,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'
import { sortServicesByMet } from '@/utils/service-diff.utils'

import { Field, FieldError } from '../ui/field'

const schema = z.object({
  selectedServiceId: z.string().min(1),
})

type CreateErrandOutput = z.output<typeof schema>

type CreateErrandDialogProps = {
  // Hides the built-in trigger button so an external control can drive `open`/`onOpenChange` instead.
  hideTrigger?: boolean
  // Preselects a service, e.g. when starting an errand directly from an open service card.
  defaultServiceId?: string
  onOpenChange?(open: boolean): void
  open?: boolean
}

export const CreateErrandDialog = ({
  defaultServiceId,
  hideTrigger,
  onOpenChange,
  open: openProp,
}: CreateErrandDialogProps = {}) => {
  const [openState, setOpenState] = useState(false)
  const { canCreateErrands } = useSubscription()
  const requestedOpen = openProp ?? openState

  /**
   * An errand is a conversation. Creating one creates a thread, and composing in a thread
   * needs an active subscription — so a trial user could start an errand and then find they
   * could not say anything in it.
   *
   * Asking for one therefore offers the subscription instead of the form. Decided here at
   * render rather than by intercepting the trigger, because this dialog is opened two ways:
   * its own button, and `open` driven from a service card. Only the render path sees both.
   */
  const open = requestedOpen && canCreateErrands
  const isSubscribeOpen = requestedOpen && !canCreateErrands

  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }

  const navigate = useNavigate()
  const { selectedIndividualId } = useIndividuals()
  const { data: serviceGroups = [] } = useServiceGroups(selectedIndividualId)
  const createErrand = useCreateErrand()
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      selectedServiceId: defaultServiceId ?? '',
    },
    resolver: zodResolver(schema),
  })

  // Which service-group tab is showing. Kept as its own state rather than left to `Tabs`'
  // `defaultValue` — that only applies once, so it always opened on the first group even
  // when `defaultServiceId` (e.g. from a service card's "Starta ärende") belongs to a
  // different one, leaving the actually-preselected radio button hidden in another tab.
  const [activeGroupId, setActiveGroupId] = useState(serviceGroups[0]?.id)

  // Keeps the preselected service — and the tab it lives in — in sync when the dialog is
  // reopened (it's a single shared instance driven from multiple entry points, e.g. service
  // cards).
  useEffect(() => {
    if (!open) return
    form.reset({ selectedServiceId: defaultServiceId ?? '' })
    const group = serviceGroups.find(sg => sg.services?.some(s => s.id === defaultServiceId))
    setActiveGroupId(group?.id ?? serviceGroups[0]?.id)
  }, [open, defaultServiceId, serviceGroups])

  const handleSubmit = async (values: CreateErrandOutput) => {
    const serviceGroup = serviceGroups.find(sg =>
      sg.services?.some(service => service.id === values.selectedServiceId),
    )
    if (!serviceGroup) return

    const { id } = await createErrand.mutateAsync({
      individualId: selectedIndividualId!,
      requestBody: {
        serviceGroups: [
          {
            serviceGroup: serviceGroup.id,
            services: [{ serviceId: values.selectedServiceId } satisfies ErrandServiceType],
          },
        ],
      },
    })
    form.reset()
    setOpen(false)
    navigate({ params: { id }, to: '/errands/$id' })
  }

  if (isSubscribeOpen) {
    return (
      <>
        {!hideTrigger && (
          <Button onClick={() => setOpen(true)}>
            <PlusIcon />
            {t('errandsPage.createErrandButton')}
          </Button>
        )}
        {/* Closing it clears the caller's state too, so a service card's "start errand"
            does not stay latched open behind the subscription dialog. */}
        <SubscribeDialog onOpenChange={next => !next && setOpen(false)} open />
      </>
    )
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button>
            <PlusIcon />
            {t('errandsPage.createErrandButton')}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent onOpenAutoFocus={event => event.preventDefault()} wide>
        <DialogHeader>
          <DialogTitle>{t('createErrandDialog.title')}</DialogTitle>
          <DialogDescription>{t('createErrandDialog.description')}</DialogDescription>
        </DialogHeader>
        <DialogScrollable>
          <FormProvider {...form}>
            <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
              {/* Service Groups Section */}
              <div className="flex flex-col gap-4">
                <Field>
                  <Controller
                    control={form.control}
                    name="selectedServiceId"
                    render={({ field }) => (
                      <RadioGroup onValueChange={field.onChange} value={field.value}>
                        <div className="card flex flex-col gap-4">
                          {/* Always a dropdown here rather than the tab row the rest of the app
                              uses for this same choice — the dialog is narrow enough that the
                              tabs reliably wrap, and there's no good breakpoint-based fix for
                              that yet. */}
                          <Select onValueChange={setActiveGroupId} value={activeGroupId}>
                            <SelectTrigger className="w-fit min-w-44">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {serviceGroups.map(sg => (
                                <SelectItem key={sg.id} value={sg.id}>
                                  {sg.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {serviceGroups
                            .filter(sg => sg.id === activeGroupId)
                            .map(sg => {
                              const sortedServices = sortServicesByMet(
                                sg.services ?? [],
                                sg.generation?.services,
                              )
                              const metById = new Map(
                                sg.generation?.services?.map(s => [s.id, s.met]),
                              )

                              return sortedServices.length > 0 ? (
                                <div className="space-y-2" key={sg.id}>
                                  {sortedServices.map((service, index) => {
                                    const met = metById.get(service.id)
                                    const previousMet =
                                      index > 0
                                        ? metById.get(sortedServices[index - 1]?.id ?? '')
                                        : undefined
                                    const showSectionLabel =
                                      met && (index === 0 || met !== previousMet)

                                    return (
                                      <Fragment key={service.id}>
                                        {showSectionLabel && (
                                          <h5 className="font-heading font-bold mt-3 first:mt-0">
                                            {met === 'recommended' && t('global.recommended')}
                                            {met === 'uncertain' && t('global.uncertain')}
                                            {met === 'not_recommended' &&
                                              t('global.notRecommended')}
                                          </h5>
                                        )}
                                        <div className="flex items-center gap-3 group">
                                          <RadioGroupItem
                                            id={`service-${service.id}`}
                                            value={service.id}
                                          />
                                          <label
                                            className="flex flex-col cursor-pointer flex-1"
                                            htmlFor={`service-${service.id}`}
                                          >
                                            <span className="">{service.title}</span>
                                          </label>
                                        </div>
                                      </Fragment>
                                    )
                                  })}
                                </div>
                              ) : null
                            })}
                        </div>
                      </RadioGroup>
                    )}
                  />
                  <FieldError>{form.formState.errors.selectedServiceId?.message}</FieldError>
                </Field>
              </div>
            </form>
          </FormProvider>
        </DialogScrollable>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setOpen(false)
                form.reset()
              }}
              type="button"
              variant="outline"
            >
              {t('global.cancel')}
            </Button>
            <Button onClick={form.handleSubmit(handleSubmit)} type="submit">
              {t('createErrandDialog.submitButton')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
